import { useState, useCallback, useRef, useEffect } from 'react';

export interface SuccessState {
  message: string | null;
  hasSuccess: boolean;
  variant: 'success' | 'info' | 'warning';
}

export interface SuccessHandler {
  message: string | null;
  hasSuccess: boolean;
  variant: 'success' | 'info' | 'warning';
  setSuccess: (message: string | null) => void;
  clearSuccess: () => void;
  showSuccess: (message: string, autoHide?: boolean, delay?: number, variant?: 'success' | 'info' | 'warning') => void;
}

/**
 * Hook personnalisé pour gérer les messages de succès avec auto-hide
 */
export const useSuccessMessage = (): SuccessHandler => {
  const [message, setMessage] = useState<string | null>(null);
  const [variant, setVariant] = useState<'success' | 'info' | 'warning'>('success');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearSuccess = useCallback(() => {
    setMessage(null);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const showSuccess = useCallback((
    successMessage: string, 
    shouldAutoHide: boolean = false, 
    delay: number = 5000,
    messageVariant: 'success' | 'info' | 'warning' = 'success'
  ) => {
    // Nettoyer le timeout précédent
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setMessage(successMessage);
    setVariant(messageVariant);

    // Configurer l'auto-hide si demandé
    if (shouldAutoHide) {
      timeoutRef.current = setTimeout(() => {
        clearSuccess();
      }, delay);
    }
  }, [clearSuccess]);

  // Nettoyer le timeout au démontage
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    message,
    hasSuccess: message !== null,
    variant,
    setSuccess: setMessage,
    clearSuccess,
    showSuccess
  };
};
